// CollaborationWorkspacePage.tsx

import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Tabs, Loader, Center, Text, Button, Flex } from "@mantine/core";
import TitleComponent from "@/components/shared/TitleComponent";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import SharedDocumentsTab from "./SharedDocumentsTab";
import MatchedProfileView from "@/pages/profile/MatchedView";
import NdaPage from "@/pages/nda/NdaPage"; // Import your existing NdaPage
import QuestionnaireTab from "./QuestionnaireTab";
import { IconArrowLeft, IconDownload } from "@tabler/icons-react";
import { generateCollaborationAlignmentReport } from "@/utils/pdfGenerator";
import { gradeFullQuestionnaire } from "@/utils/gradingEngine";
import { getQuestionnaireId } from "@/utils/questionnaireMapping";
import type { FullGradingResult, Answer } from "@/utils/gradingEngine.types";
import q1 from "@/data/questionnaires/questionnaire_1.json";
import q2 from "@/data/questionnaires/questionnaire_2.json";
import q3 from "@/data/questionnaires/questionnaire_3.json";
import q4 from "@/data/questionnaires/questionnaire_4.json";
import q5 from "@/data/questionnaires/questionnaire_5.json";
import q6 from "@/data/questionnaires/questionnaire_6.json";

const QUESTIONNAIRE_MAP: Record<string, any> = {
  [q1.id]: q1,
  [q2.id]: q2,
  [q3.id]: q3,
  [q4.id]: q4,
  [q5.id]: q5,
  [q6.id]: q6,
};

const WORKSPACE_TABS = new Set(["overview", "documents", "nda", "questionnaire"]);

function tabFromSearchParams(searchParams: URLSearchParams): string | null {
  const t = searchParams.get("tab");
  return t && WORKSPACE_TABS.has(t) ? t : null;
}

export default function CollaborationWorkspacePage() {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userDetails, hasProfile } = useUserContext();

  const initialTab = tabFromSearchParams(searchParams) ?? "overview";
  const [activeTab, setActiveTab] = useState<string | null>(initialTab);

  const autoOpenQuestionnaireModal = useMemo(
    () => searchParams.get("tab") === "questionnaire",
    [searchParams]
  );

  const handleWorkspaceTabChange = useCallback(
    (value: string | null) => {
      setActiveTab(value);
      if (value !== "questionnaire") {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("tab");
            return next;
          },
          { replace: true }
        );
      }
    },
    [setSearchParams]
  );

  // Loading states
  const [loadingCollaboration, setLoadingCollaboration] = useState(true);
  const [loadingCompany, setLoadingCompany] = useState(false);

  // Data states
  const [collaboration, setCollaboration] = useState<any>(null);
  const [company, setCompany] = useState<any>(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [questionnaireMySubmitted, setQuestionnaireMySubmitted] = useState<boolean | null>(null);

  // Derived state
  const otherCompanyId =
    collaboration?.requesterCompanyId === userDetails?.company?.id
      ? collaboration?.receiverCompanyId
      : collaboration?.requesterCompanyId;

  // 1. Fetch Collaboration (Always needed for context)
  const fetchCollaboration = useCallback(async () => {
    if (!hasProfile || !id) return;
    setLoadingCollaboration(true);
    try {
      const collabData = await trpc.collaboration.getById.query({ matchId: id });
      setCollaboration(collabData);
    } catch (err) {
      console.error("Error fetching collaboration:", err);
      handleTrpcError(err);
    } finally {
      setLoadingCollaboration(false);
    }
  }, [id, hasProfile]);

  // 2. Fetch Company (Lazy load)
  const fetchCompany = useCallback(async (companyId: string) => {
    setLoadingCompany(true);
    try {
      const companyData = await trpc.company.getById.query({ companyId });
      setCompany(companyData);
    } catch (err) {
      console.error("Error fetching company:", err);
      handleTrpcError(err);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  // Track visited tabs to lazy load content but keep it mounted
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => {
    const s = new Set<string>(["overview"]);
    if (initialTab !== "overview") s.add(initialTab);
    return s;
  });

  useEffect(() => {
    setQuestionnaireMySubmitted(null);
    const t = tabFromSearchParams(searchParams);
    setActiveTab(t ?? "overview");
    setVisitedTabs(() => {
      const s = new Set<string>(["overview"]);
      if (t && t !== "overview") s.add(t);
      return s;
    });
  }, [id]);

  const collaborationHasQuestionnaire = useMemo(() => {
    try {
      if (!collaboration?.requesterRole || !collaboration?.expectedPartnerRole) return false;
      getQuestionnaireId(collaboration.requesterRole, collaboration.expectedPartnerRole);
      return true;
    } catch {
      return false;
    }
  }, [collaboration?.requesterRole, collaboration?.expectedPartnerRole]);

  const fetchQuestionnaireMySubmission = useCallback(async () => {
    if (!id) return;
    try {
      const data = await trpc.collaboration.getQuestionnaireResponses.query({ matchId: id });
      setQuestionnaireMySubmitted(!!data.mySubmittedAt);
    } catch {
      setQuestionnaireMySubmitted(null);
    }
  }, [id]);

  useEffect(() => {
    if (!collaboration || !id || activeTab !== "overview") return;
    fetchQuestionnaireMySubmission();
  }, [collaboration, id, activeTab, fetchQuestionnaireMySubmission]);

  const handleGoToQuestionnaireTab = useCallback(() => {
    setActiveTab("questionnaire");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "questionnaire");
        return next;
      },
      { replace: true }
    );
    setVisitedTabs((prev) => new Set(prev).add("questionnaire"));
  }, [setSearchParams]);

  // Effect: Fetch Collaboration on mount
  useEffect(() => {
    fetchCollaboration();
  }, [fetchCollaboration]);

  // Effect: Fetch Company when needed (Overview tab or explicit check) and update visited tabs
  useEffect(() => {
    if (activeTab) {
      setVisitedTabs((prev) => {
        if (prev.has(activeTab)) return prev;
        return new Set(prev).add(activeTab);
      });
    }

    if (activeTab === "overview" && otherCompanyId && !company && !loadingCompany) {
      fetchCompany(otherCompanyId);
    }
  }, [activeTab, otherCompanyId, company, loadingCompany, fetchCompany]);

  if (loadingCollaboration) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent title="Collaboration Workspace" />
        <Center py="xl" mih="50vh">
          <Loader size="lg" />
        </Center>
      </Box>
    );
  }

  if (!collaboration) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent title="Collaboration Workspace" />
        <Center py="xl" mih="50vh">
          <Text c="slate.6">Collaboration not found</Text>
        </Center>
      </Box>
    );
  }

  const handleDownloadProfile = async () => {
    setDownloadingPDF(true);
    try {
      // Fetch Party A (own company)
      const partyACompany = await trpc.company.getById.query({ companyId: userDetails!.company!.id });

      // Fetch Party B (other company), reuse if already loaded
      let partyBCompany = company;
      if (!partyBCompany && otherCompanyId) {
        partyBCompany = await trpc.company.getById.query({ companyId: otherCompanyId });
        setCompany(partyBCompany);
      }

      if (!partyACompany || !partyBCompany) return;

      // Fetch shared documents
      const sharedDocs = await trpc.collaboration.listDocuments.query({ matchId: id });

      const collaborationType =
        collaboration?.requesterRole && collaboration?.expectedPartnerRole
          ? `${collaboration.requesterRole} / ${collaboration.expectedPartnerRole}`
          : undefined;

      // Resolve grading result and section titles for section 7
      let gradingResult: FullGradingResult | null = null;
      let sectionTitleMap: Record<string, string> = {};
      let myAnswersForPdf: Record<string, any> | undefined;
      let partnerAnswersForPdf: Record<string, any> | undefined;
      let questionOptionMap: Record<string, { type: string; options: Array<{ id: string; label: string }> }> | undefined;

      if (collaboration?.requesterRole && collaboration?.expectedPartnerRole) {
        try {
          const questionnaireId = getQuestionnaireId(
            collaboration.requesterRole,
            collaboration.expectedPartnerRole
          );
          const questionnaire = QUESTIONNAIRE_MAP[questionnaireId];

          if (questionnaire) {
            // Build section title map and question option map
            questionOptionMap = {};
            for (const section of questionnaire.sections ?? []) {
              sectionTitleMap[section.id] = section.title;
              for (const q of section.questions) {
                questionOptionMap[q.id] = { type: q.type, options: q.options.map((o: any) => ({ id: o.id, label: o.label })) };
              }
            }

            // Fetch questionnaire responses
            const responseData = await trpc.collaboration.getQuestionnaireResponses.query({ matchId: id });

            if (responseData.bothSubmitted && responseData.myAnswers && responseData.partnerAnswers) {
              myAnswersForPdf = responseData.myAnswers as Record<string, any>;
              partnerAnswersForPdf = responseData.partnerAnswers as Record<string, any>;

              // Use cached grading result from server if available
              if (responseData.gradingResult) {
                gradingResult = responseData.gradingResult as unknown as FullGradingResult;
              } else {
                const isRequester = collaboration.requesterCompanyId === userDetails?.company?.id;
                const answersA = isRequester
                  ? (responseData.myAnswers as unknown as Record<string, Answer>)
                  : (responseData.partnerAnswers as unknown as Record<string, Answer>);
                const answersB = isRequester
                  ? (responseData.partnerAnswers as unknown as Record<string, Answer>)
                  : (responseData.myAnswers as unknown as Record<string, Answer>);
                gradingResult = gradeFullQuestionnaire(questionnaire, answersA, answersB);
              }
            }
          }
        } catch (err) {
          console.error("Failed to compute grading for PDF:", err);
        }
      }

      await generateCollaborationAlignmentReport({
        partyA: partyACompany,
        partyB: partyBCompany,
        sharedDocuments: sharedDocs,
        matchId: id,
        collaborationType,
        requesterRole: collaboration?.requesterRole,
        expectedPartnerRole: collaboration?.expectedPartnerRole,
        gradingResult,
        sectionTitleMap,
        myAnswers: myAnswersForPdf,
        partnerAnswers: partnerAnswersForPdf,
        questionOptionMap,
      });
    } catch (error) {
      console.error("Failed to generate report:", error);
    } finally {
      setDownloadingPDF(false);
    }
  };

  return (
    <Box style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Header with Back Button and Title */}
      <Flex align="center" gap="md" px="md" py="md" style={{ borderBottom: "1px solid #E5E7EB", flexShrink: 0 }}>
        <Button
          variant="outline"
          color="gray"
          p={8}
          onClick={() => navigate("/collaborations")}
          styles={{
            root: {
              borderColor: "#E5E7EB",
              borderRadius: 8,
              minWidth: "auto",
              height: "auto",
            },
          }}
        >
          <IconArrowLeft size={18} color="#374151" />
        </Button>
        <Text fw={500} fz="xl">
          Collaboration Workspace
        </Text>
        <Button
          ml="auto"
          variant="outline"
          leftSection={<IconDownload size={18} />}
          loading={downloadingPDF}
          onClick={handleDownloadProfile}
        >
          Download Profile
        </Button>
      </Flex>

      <Box px="md" pt="md" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleWorkspaceTabChange}
          style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
        >
          <Tabs.List style={{ flexShrink: 0 }}>
            <Tabs.Tab
              value="overview"
              style={{
                color: activeTab === "overview" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-9)",
              }}
            >
              Overview
            </Tabs.Tab>
            <Tabs.Tab
              value="documents"
              style={{
                color: activeTab === "documents" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-9)",
              }}
            >
              Shared Documents
            </Tabs.Tab>
            <Tabs.Tab
              value="nda"
              style={{
                color: activeTab === "nda" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-9)",
              }}
            >
              NDA
            </Tabs.Tab>
            <Tabs.Tab
              value="questionnaire"
              style={{
                color:
                  activeTab === "questionnaire" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-9)",
              }}
            >
              Questionnaire
            </Tabs.Tab>
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {loadingCompany ? (
              <Center h="100%">
                <Loader />
              </Center>
            ) : (
              company && (
                <MatchedProfileView
                  company={company}
                  containerSize="fluid"
                  questionnaireCta={
                    activeTab === "overview" &&
                    collaborationHasQuestionnaire &&
                    questionnaireMySubmitted !== null
                      ? questionnaireMySubmitted === false
                        ? { type: "start", onClick: handleGoToQuestionnaireTab }
                        : { type: "view", onClick: handleGoToQuestionnaireTab }
                      : null
                  }
                />
              )
            )}
          </Tabs.Panel>

          {/* Documents Tab */}
          <Tabs.Panel value="documents" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {activeTab === "documents" && (
              <SharedDocumentsTab matchId={id} userCompanyName={userDetails?.company?.legalName} />
            )}
          </Tabs.Panel>

          {/* NDA Tab - Reuse existing NdaPage */}
          <Tabs.Panel value="nda" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {visitedTabs.has("nda") && (
              <NdaPage embedded otherCompanyId={otherCompanyId} collaboration={collaboration} />
            )}
          </Tabs.Panel>

          {/* Questionnaire Tab — always mounted so the modal can show on page load */}
          <Tabs.Panel value="questionnaire" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {collaboration && userDetails?.company?.id && (
              <QuestionnaireTab
                matchId={id}
                collaboration={collaboration}
                userCompanyId={userDetails.company.id}
                autoOpenModal={autoOpenQuestionnaireModal}
              />
            )}
          </Tabs.Panel>
        </Tabs>
      </Box>
    </Box>
  );
}
